import React from 'react';
import { Monitor, Smartphone, Tablet, Globe } from 'lucide-react';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { useGA4 } from '../../contexts/GA4Context';
import TimeRangeSelector from '../../components/analytics/TimeRangeSelector';
import MetricCard from '../../components/analytics/MetricCard';
import DataTable from '../../components/analytics/DataTable';
import { formatNumber, formatPercentage, formatDuration } from '../../utils/analytics-helpers';
import './TechnologyAnalytics.css';

const TechnologyAnalytics = () => {
  const { loading, error, metrics } = useGA4();

  // Mock device data - replace with actual GA4 data when available
  const deviceData = [
    { device: 'Desktop', sessions: 450, users: 320, percentage: 45 },
    { device: 'Mobile', sessions: 380, users: 270, percentage: 38 },
    { device: 'Tablet', sessions: 170, users: 110, percentage: 17 }
  ];

  const browserData = [
    { browser: 'Chrome', sessions: 520, users: 380 },
    { browser: 'Safari', sessions: 280, users: 190 },
    { browser: 'Firefox', sessions: 120, users: 85 },
    { browser: 'Edge', sessions: 80, users: 60 }
  ];

  const DEVICE_COLORS = {
    'Desktop': '#3B82F6',
    'Mobile': '#8B5CF6',
    'Tablet': '#EC4899'
  };

  const BROWSER_COLORS = {
    'Chrome': '#3B82F6',
    'Safari': '#8B5CF6',
    'Firefox': '#EC4899',
    'Edge': '#F59E0B',
    'Other': '#6B7280'
  };

  // Table columns
  const technologyColumns = [
    { key: 'name', label: 'Technology', sortable: true },
    { key: 'category', label: 'Category', sortable: true },
    { key: 'sessions', label: 'Sessions', sortable: true, format: (val) => formatNumber(val) },
    { key: 'users', label: 'Users', sortable: true, format: (val) => formatNumber(val) },
    { key: 'avgDuration', label: 'Avg Duration', sortable: true, format: (val) => formatDuration(val) }
  ];

  // Combined technology data for table
  const technologyTableData = [
    ...deviceData.map(d => ({ ...d, name: d.device, category: 'Device', avgDuration: 180 })),
    ...browserData.map(b => ({ ...b, name: b.browser, category: 'Browser', avgDuration: 200 }))
  ];

  return (
    <div className="technology-analytics">
      <div className="technology-header">
        <div>
          <h2>Technology Analytics</h2>
          <p className="technology-subtitle">Device types, browsers, and operating systems</p>
        </div>
        <TimeRangeSelector />
      </div>

      {error && (
        <div className="error-banner">
          <p>{error}</p>
        </div>
      )}

      {loading ? (
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading technology data...</p>
        </div>
      ) : (
        <>
          {/* Device Overview Cards */}
          <div className="metrics-grid">
            <MetricCard
              title="Desktop Sessions"
              value={deviceData[0]?.sessions || 0}
              format="number"
              icon={Monitor}
              subtitle={`${deviceData[0]?.percentage || 0}% of traffic`}
            />
            <MetricCard
              title="Mobile Sessions"
              value={deviceData[1]?.sessions || 0}
              format="number"
              icon={Smartphone}
              subtitle={`${deviceData[1]?.percentage || 0}% of traffic`}
            />
            <MetricCard
              title="Tablet Sessions"
              value={deviceData[2]?.sessions || 0}
              format="number"
              icon={Tablet}
              subtitle={`${deviceData[2]?.percentage || 0}% of traffic`}
            />
            <MetricCard
              title="Total Browsers"
              value={browserData.length}
              format="number"
              icon={Globe}
              subtitle="Detected browsers"
            />
          </div>

          {/* Device & Browser Charts */}
          <div className="charts-row">
            <div className="chart-container">
              <h3>Device Distribution</h3>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={deviceData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ device, percentage }) =>
                      `${device} ${percentage}%`
                    }
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="sessions"
                  >
                    {deviceData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={DEVICE_COLORS[entry.device] || '#6B7280'}
                      />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'white',
                      border: '1px solid #E5E7EB',
                      borderRadius: '8px',
                      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>

            <div className="chart-container">
              <h3>Browser Usage</h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={browserData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                  <XAxis dataKey="browser" tick={{ fill: '#6B7280', fontSize: 12 }} />
                  <YAxis tick={{ fill: '#6B7280', fontSize: 12 }} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'white',
                      border: '1px solid #E5E7EB',
                      borderRadius: '8px',
                      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                    }}
                  />
                  <Bar dataKey="sessions" fill="#3B82F6">
                    {browserData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={BROWSER_COLORS[entry.browser] || '#6B7280'}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Device Comparison */}
          <div className="comparison-section">
            <h3>Device Performance Comparison</h3>
            <div className="device-comparison">
              {deviceData.map((device, index) => (
                <div key={index} className="device-item">
                  <div className="device-header">
                    <div className="device-icon" style={{
                      background: `${DEVICE_COLORS[device.device]}20`,
                      color: DEVICE_COLORS[device.device]
                    }}>
                      {device.device === 'Desktop' && <Monitor size={24} />}
                      {device.device === 'Mobile' && <Smartphone size={24} />}
                      {device.device === 'Tablet' && <Tablet size={24} />}
                    </div>
                    <h4>{device.device}</h4>
                  </div>
                  <div className="device-stats">
                    <div className="device-stat">
                      <span className="stat-label">Sessions</span>
                      <span className="stat-value">{formatNumber(device.sessions)}</span>
                    </div>
                    <div className="device-stat">
                      <span className="stat-label">Users</span>
                      <span className="stat-value">{formatNumber(device.users)}</span>
                    </div>
                    <div className="device-stat">
                      <span className="stat-label">Share</span>
                      <span className="stat-value">{device.percentage}%</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Technology Details Table */}
          <div className="table-container">
            <DataTable
              data={technologyTableData}
              columns={technologyColumns}
              title="All Technology Details"
              searchable={true}
              exportable={true}
              pageSize={25}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default TechnologyAnalytics;
